import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getNetworkConfig } from "@/lib/api";
import { executePoolSwap, minReceived, swapErrorMessage, SLIPPAGE_PERCENT } from "@/lib/poolSwap";
import { PriceChart } from "@/components/exchange/PriceChart";
import {
  TradeHeader, AmountBox, TokenIcon, QuoteError, QuoteBreakdown, QuoteRow, QuoteDivider, FeeLabel, SubmitButton, TxLink,
} from "@/components/exchange/TradeParts";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TOKENS } from "@/constants/network";
import { EXCHANGE } from "@/constants/testIds";
import { useWallet } from "@/contexts/WalletContext";
import { useLiveQuote, useTradeExecution } from "@/hooks/useTrade";
import { ArrowDownUp } from "lucide-react";
import { motion } from "framer-motion";
import { FADE_UP_SLOW } from "@/lib/motion";
import { GasHint, GAS_UNITS } from "@/components/exchange/GasHint";

const isLivePool = (quote) => quote?.source === "fsc-pool" || quote?.source === "fsc-lp-pool";

const TokenSelect = ({ value, onChange, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className="w-[130px] bg-[#0A0A0A] border-[#2A2411] text-white shrink-0">
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="bg-[#0A0A0A] border-[#2A2411]">
      {TOKENS.map((t) => (
        <SelectItem key={t.symbol} value={t.symbol} className="text-white hover:text-white focus:text-white">
          <div className="flex items-center gap-2"><TokenIcon symbol={t.symbol} />{t.symbol}</div>
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const OutputValue = ({ loading, quote }) => {
  if (loading) return <span className="text-zinc-600">...</span>;
  if (quote) return quote.outputAmount;
  return <span className="text-zinc-700">0.0</span>;
};

const PoolRows = ({ quote, toToken, usdtDecimals }) => (
  <>
    <QuoteRow label="Price impact" value={`${quote.priceImpact}%`} testId="swap-price-impact"
      valueClass={`font-mono ${quote.priceImpact > 2 ? "text-red-400" : "text-white"}`} />
    <QuoteRow label={`Min. received (${SLIPPAGE_PERCENT}% slippage)`} testId="swap-min-received"
      value={`${(Number(minReceived(quote)) / 10 ** (toToken === "FMI" ? 18 : usdtDecimals)).toFixed(6)} ${toToken}`} />
  </>
);

const SwapBreakdown = ({ quote, fromToken, toToken, usdtDecimals }) => (
  <QuoteBreakdown>
    <QuoteRow label="Rate" value={`1 ${fromToken} = ${quote.rate} ${toToken}`} />
    {isLivePool(quote) && <QuoteRow label="Route" value={quote.source === "fsc-lp-pool" ? "Community liquidity pool" : "Main FSC pool"} testId="swap-route" />}
    <QuoteRow label={<FeeLabel percent={quote.feePercent} />} value={`${quote.feeAmount} ${quote.feeToken || toToken}`} testId={EXCHANGE.feeAmount} />
    {isLivePool(quote) && <PoolRows quote={quote} toToken={toToken} usdtDecimals={usdtDecimals} />}
    <QuoteDivider />
    <QuoteRow strong label="You Receive" value={`${quote.outputAmount} ${toToken}`} valueClass="text-[#D4AF37] font-display font-bold" />
    {quote.isDemo && <p className="text-xs text-zinc-600 mt-1">Prices are simulated for demo purposes</p>}
  </QuoteBreakdown>
);

export default function SwapPage() {
  const [fromToken, setFromToken] = useState("FMI");
  const [toToken, setToToken] = useState("USDT");
  const [amount, setAmount] = useState("");
  const { address, getProvider, refreshBalances } = useWallet();
  const { data: config } = useQuery({
    queryKey: ["networkConfig"], queryFn: getNetworkConfig,
    refetchInterval: (q) => (q.state.data?.swap?.address ? false : 3000),
  });
  const livePair = Boolean(config?.swap?.address) && [fromToken, toToken].sort().join() === "FMI,USDT";
  const { quote, setQuote, quoteError, loading } = useLiveQuote("swap", amount, fromToken, toToken);
  const trade = useTradeExecution({ address, refreshBalances, onDone: () => { setAmount(""); setQuote(null); } });

  const flipTokens = () => {
    setFromToken(toToken);
    setToToken(fromToken);
    setQuote(null);
  };

  const handleExecute = () =>
    trade.run({
      live: isLivePool(quote),
      execute: (onStep) => executePoolSwap({ eip1193: getProvider(), quote, usdtAddress: config.usdt.address, onStep }),
      successMessage: `Swapped ${quote?.inputAmount} ${quote?.fromToken} for ~${quote?.outputAmount} ${quote?.toToken}`,
      errorMessage: swapErrorMessage,
      demoMessage: "Swap executed successfully (Demo)",
    });

  return (
    <div data-testid={EXCHANGE.container} className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP_SLOW}>
        <TradeHeader accent="Swap" title="Tokens" subtitle="Trade tokens on FMI Smart Chain" live={livePair} liveTestId="swap-live-badge"
          liveLabel="Live on FSC pool" demoLabel="Demo Mode (only FMI ⇄ USDT is live)" />
        <Card className="border-[#2A2411] bg-[#0A0A0A]/80 backdrop-blur-xl gold-glow">
          <CardContent className="p-5 space-y-4">
            <AmountBox label="You Pay" amount={amount} onChange={setAmount}>
              <TokenSelect value={fromToken} onChange={setFromToken} testId={EXCHANGE.fromTokenSelect} />
            </AmountBox>
            <div className="flex justify-center -my-1">
              <button data-testid="exchange-flip-btn" onClick={flipTokens}
                className="w-10 h-10 rounded-full bg-[#0A0A0A] border border-[#2A2411] flex items-center justify-center text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors">
                <ArrowDownUp size={16} />
              </button>
            </div>
            <div className="p-4 rounded-xl bg-[#050505] border border-[#2A2411]">
              <span className="text-xs text-zinc-500">You Receive</span>
              <div className="flex items-center gap-3 mt-2">
                <p data-testid={EXCHANGE.outputAmount} className="text-2xl font-display font-bold text-white flex-1"><OutputValue loading={loading} quote={quote} /></p>
                <TokenSelect value={toToken} onChange={setToToken} testId={EXCHANGE.toTokenSelect} />
              </div>
            </div>
            <QuoteError error={quoteError} loading={loading} />
            {quote && <SwapBreakdown quote={quote} fromToken={fromToken} toToken={toToken} usdtDecimals={config?.usdt?.decimals ?? 6} />}
            <SubmitButton testId={EXCHANGE.swapSubmit} onClick={handleExecute} disabled={!quote || trade.executing || !amount}
              executing={trade.executing} step={trade.step} address={address} amount={amount} connectLabel="Connect Wallet">
              Swap
            </SubmitButton>
            {address && isLivePool(quote) && (
              <GasHint gasUnits={fromToken === "FMI" ? GAS_UNITS.swapFmi : GAS_UNITS.swapUsdt + GAS_UNITS.approve}
                note={fromToken === "FMI" ? "paid in FMI on top of the amount" : "includes a one-time USDT approval"} testId="swap-gas-hint" />
            )}
            <TxLink hash={trade.lastTx} testId="swap-tx-link" label="View swap transaction" />
          </CardContent>
        </Card>
        {config?.swap?.address && <PriceChart />}
      </motion.div>
    </div>
  );
}
